import { Flex, Image, Tag } from "antd";
import Title from "antd/es/typography/Title";
import Text from "antd/es/typography/Text";
import { isMobile } from "react-device-detect";
import {
  RightOutlined,
  CloudServerOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import ImgHwFront from "../assets/images/hardware_front.png";
import ImgHwSide from "../assets/images/hardware_side.png";
import ImgHwBack from "../assets/images/hardware_back.png";
import ImgHwDisplayed from "../assets/images/hardware_front_displayed.png";
import { Link } from "@inertiajs/react";

const ACCENT = "#4096ff";
const ACCENT_LIGHT = "#4096ff15";
const TEXT_PRIMARY = "#1a2332";
const TEXT_SECONDARY = "#556678";

const productImages = [
  { src: ImgHwFront, title: "Tampak Depan" },
  { src: ImgHwSide, title: "Tampak Samping" },
  { src: ImgHwBack, title: "Tampak Belakang" },
  { src: ImgHwDisplayed, title: "Tampilan Saat Aktif" },
];

const kontenFeatures = [
  "Animasi ramah anak",
  "6 Topik Bencana Alam",
  "Simulasi interaktif",
  "Evaluasi berbasis permainan",
];

const softwareFeatures = ["Dashboard monitoring pendidik", "Rapor Otomatis"];

const productLines = [
  {
    name: "Kiversa Edge",
    icon: <CloudServerOutlined />,
    color: "#52c41a",
    desc: "Zero-Connectivity",
  },
  {
    name: "Kiversa Flexi-Sub",
    icon: <RocketOutlined />,
    color: ACCENT,
    desc: "Berdasarkan Langganan",
  },
  {
    name: "Kiversa Pro-License",
    icon: <SafetyCertificateOutlined />,
    color: "#722ed1",
    desc: "Lisensi Khusus",
  },
];

function ProductSection() {
  const previewItems = productImages.map((img) => ({
    src: img.src,
    alt: img.title,
  }));

  return (
    <section
      style={{
        width: "100%",
        padding: isMobile ? "60px 0 64px" : "100px 0 80px",
        position: "relative",
        overflow: "hidden",
        background: "#e2e8f0",
      }}
    >
      {/* Decorative */}
      <div
        style={{
          position: "absolute",
          top: -60,
          right: -100,
          width: 360,
          height: 360,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(64,150,255,0.06) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: -40,
          left: -80,
          width: 300,
          height: 300,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, rgba(114,46,209,0.05) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <Flex
        vertical
        align="center"
        style={{
          width: "100%",
          maxWidth: 1200,
          margin: "0 auto",
          padding: `0 ${isMobile ? 16 : 24}px`,
          position: "relative",
          zIndex: 1,
        }}
        gap={isMobile ? 28 : 40}
      >
        {/* Header */}
        <Flex vertical align="center" gap={isMobile ? 10 : 14}>
          <Title
            level={2}
            style={{
              margin: 0,
              fontSize: isMobile ? 26 : "clamp(28px, 4vw, 40px)",
              fontWeight: 800,
              lineHeight: 1.2,
              textAlign: "center",
              color: TEXT_PRIMARY,
              fontFamily: "'Quicksand', sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            Produk <span style={{ color: ACCENT }}>Kami</span>
          </Title>
          <Text
            style={{
              maxWidth: 560,
              fontSize: isMobile ? 14 : 16,
              lineHeight: 1.7,
              color: TEXT_SECONDARY,
              textAlign: "center",
              fontFamily: "'Quicksand', sans-serif",
            }}
          >
            Hardware interaktif dilengkapi konten edukasi kebencanaan dan
            software manajemen pendidikan yang terintegrasi.
          </Text>
        </Flex>

        {/* Product Lines Preview */}
        <Flex wrap="wrap" justify="center" gap={isMobile ? 8 : 12}>
          {productLines.map((p, i) => (
            <Tag
              key={i}
              style={{
                borderRadius: 100,
                padding: isMobile ? "4px 10px" : "6px 16px",
                fontSize: isMobile ? 11 : 13,
                fontWeight: 600,
                fontFamily: "'Quicksand', sans-serif",
                background: `${p.color}0d`,
                color: p.color,
                border: `1px solid ${p.color}28`,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                cursor: "default",
              }}
            >
              {p.icon}
              {p.name}
              <span
                style={{
                  fontSize: isMobile ? 9 : 11,
                  fontWeight: 400,
                  opacity: 0.7,
                  marginLeft: 2,
                  display: isMobile ? "none" : "inline",
                }}
              >
                — {p.desc}
              </span>
            </Tag>
          ))}
        </Flex>

        {/* Main Card */}
        <div
          style={{
            width: "100%",
            background: "#ffffff",
            borderRadius: isMobile ? 16 : 20,
            border: "1px solid rgba(0,0,0,0.05)",
            boxShadow: "0 6px 32px rgba(0, 0, 0, 0.06)",
            overflow: "hidden",
          }}
        >
          <Image.PreviewGroup items={previewItems}>
            <Flex
              gap={isMobile ? 8 : 12}
              wrap={isMobile}
              style={{
                width: "100%",
                padding: isMobile ? "10px 10px 0" : "16px 16px 0",
              }}
            >
              {productImages.map((img, i) => (
                <div
                  key={i}
                  className="product-img-card"
                  style={{
                    flex: isMobile ? "1 1 calc(50% - 4px)" : "1 1 0",
                    maxWidth: isMobile ? "calc(50% - 4px)" : undefined,
                    minWidth: 0,
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    borderRadius: isMobile ? 10 : 12,
                  }}
                >
                  <Image
                    src={img.src}
                    alt={img.title}
                    style={{
                      width: "100%",
                      height: "auto",
                      display: "block",
                      aspectRatio: "3 / 4",
                      objectFit: "cover",
                      transition: "transform 0.4s ease",
                    }}
                    preview={{
                      mask: (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "100%",
                            height: "100%",
                            background: "rgba(0, 0, 0, 0.5)",
                            padding: 16,
                          }}
                        >
                          <span
                            style={{
                              color: "#fff",
                              fontSize: 14,
                              fontWeight: 700,
                              fontFamily: "'Quicksand', sans-serif",
                              textAlign: "center",
                            }}
                          >
                            {img.title}
                          </span>
                        </div>
                      ),
                    }}
                  />
                </div>
              ))}
            </Flex>
          </Image.PreviewGroup>

          <div
            style={{
              height: 1,
              background: "rgba(0,0,0,0.06)",
              margin: `${isMobile ? 10 : 16}px 0 0`,
            }}
          />

          <Flex
            gap={isMobile ? 20 : 32}
            justify="center"
            wrap={isMobile}
            style={{
              padding: isMobile ? "16px 20px" : "24px 28px",
            }}
          >
            <Flex vertical gap={8} style={{ flex: "1 1 200px" }}>
              <Tag
                color="blue"
                style={{
                  width: "fit-content",
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  padding: "1px 10px",
                  borderRadius: 100,
                  fontFamily: "'Quicksand', sans-serif",
                }}
              >
                KONTEN
              </Tag>
              {kontenFeatures.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    fontFamily: "'Quicksand', sans-serif",
                  }}
                >
                  <span
                    style={{
                      color: ACCENT,
                      fontSize: isMobile ? 12 : 14,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                  {f}
                </div>
              ))}
            </Flex>

            <Flex vertical gap={8} style={{ flex: "1 1 200px" }}>
              <Tag
                color="purple"
                style={{
                  width: "fit-content",
                  margin: 0,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.1em",
                  padding: "1px 10px",
                  borderRadius: 100,
                  fontFamily: "'Quicksand', sans-serif",
                }}
              >
                SOFTWARE
              </Tag>
              {softwareFeatures.map((f, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: isMobile ? 12 : 13,
                    fontWeight: 600,
                    color: TEXT_PRIMARY,
                    fontFamily: "'Quicksand', sans-serif",
                  }}
                >
                  <span
                    style={{
                      color: "#722ed1",
                      fontSize: isMobile ? 12 : 14,
                      flexShrink: 0,
                    }}
                  >
                    ✓
                  </span>
                  {f}
                </div>
              ))}
            </Flex>
          </Flex>
        </div>

        {/* CTA Button */}
        <Link
          href="/product"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            width: isMobile ? "100%" : "auto",
            padding: isMobile ? "13px 24px" : "14px 36px",
            borderRadius: isMobile ? 12 : 14,
            background: ACCENT,
            color: "#fff",
            fontWeight: 700,
            fontSize: isMobile ? 14 : 15,
            fontFamily: "'Quicksand', sans-serif",
            textDecoration: "none",
            boxShadow: "0 6px 20px rgba(64,150,255,0.35)",
            transition: "all 0.25s ease",
            letterSpacing: "0.01em",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-3px)";
            e.currentTarget.style.boxShadow =
              "0 10px 28px rgba(64,150,255,0.45)";
            e.currentTarget.style.background = "#1677ff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow =
              "0 6px 20px rgba(64,150,255,0.35)";
            e.currentTarget.style.background = ACCENT;
          }}
        >
          Lihat Semua Solusi Produk
          <RightOutlined style={{ fontSize: 13 }} />
        </Link>
      </Flex>

      {/* Hanya hover override, tanpa media query */}
      <style>{`
        .product-img-card .ant-image {
          display: block !important;
          width: 100% !important;
        }
        .product-img-card:hover img {
          transform: scale(1.03) !important;
        }
      `}</style>
    </section>
  );
}

export default ProductSection;
